/**
 * Checks if the application is running in an Electron environment.
 *
 * @returns true if running in Electron app, false if running in browser
 */
export const isElectronApp = (): boolean => {
  return typeof window !== "undefined" && window.electronAPI !== undefined;
};

/**
 * Checks if Windows domain credential caching is available in the current
 * environment. This is only true when running inside an Electron app version
 * that exposes the `cacheDomainCredentials` hook. Use this to feature-gate the
 * Amazon Account Caching step so it only appears when the native capability is
 * present (and stays hidden on older Electron builds and in the browser).
 *
 * @returns true if the cacheDomainCredentials hook is available
 */
export const isCredentialCachingAvailable = (): boolean => {
  return (
    typeof window !== "undefined" &&
    !!window.electronAPI &&
    typeof window.electronAPI.cacheDomainCredentials === "function"
  );
};

/**
 * Whether this build exposes the whole observe bridge.
 *
 * All three hooks are required: a build that cannot be told which step it is on would file frames
 * against the wrong one, including the never-captured identity step.
 */
export const isSessionObserveAvailable = (): boolean => {
  return (
    typeof window !== "undefined" &&
    !!window.electronAPI &&
    typeof window.electronAPI.startObserveSession === "function" &&
    typeof window.electronAPI.stopObserveSession === "function" &&
    typeof window.electronAPI.setObserveStep === "function"
  );
};

/**
 * Whether this build can run the macOS permissions flow.
 *
 * Both are checked together: the checklist is useless without a probe, and a hire sent to System
 * Settings with no way to reopen it is worse than never offering the flow. Only what the flow
 * actually calls is required -- the widget ships to production ahead of the desktop app, so a gate
 * demanding a method nobody calls would withhold the walkthrough from builds that could run it.
 */
export const isPermissionFlowAvailable = (): boolean => {
  return (
    /* Unfalsifiable under jsdom, which always defines `window`; it guards a non-browser import. */
    typeof window !== "undefined" &&
    !!window.electronAPI &&
    typeof window.electronAPI.probeAgentCapability === "function" &&
    typeof window.electronAPI.openPermissionSettings === "function"
  );
};

/**
 * Whether this desktop build lets the web app choose which Cisco VPN preference is
 * seeded. Absent on builds that predate the channel, which seed the legacy group
 * unconditionally, so its absence means the flag cannot take effect.
 */
export const isVpnPreferenceSeedControlAvailable = (): boolean => {
  return (
    typeof window !== "undefined" &&
    !!window.electronAPI &&
    typeof window.electronAPI.setVpnPreferenceVariant === "function"
  );
};
